// built-from: /chat-component v3 impl:chat-component role:implementation
// Orchestrator hook: composes the per-domain hooks below and owns the
// session-lifecycle concerns that don't belong to any one of them
// (load effect, createSession, restartSession, downloadTranscript,
// handleTextMessage, ref-syncing, public return shape).

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { parseDsn, DsnModel } from '../utils/summaryParser';
import { coerceNewModel, coerceDiscoveryFeatures } from '../utils/sessionCoercers';
import type { NewModel } from '../utils/flowModel';
import type {
  ConversationMode,
  CreateSessionRefs,
  DoneModel,
  SessionData,
  SessionPhase,
  SpineStep,
  UseSessionReturn,
} from './sessionTypes';
import { useDiscoveryFeatures } from './useDiscoveryFeatures';
import { useChat } from './useChat';

// Re-export public types so consumers that import from
// '../hooks/useSession' (FeaturesPanel, DebugPanel, CodeComparisonPanel,
// WatchModeView, featureSpecMarkdown) keep working unchanged.
export type {
  AttachmentRecord,
  ConversationMessage,
  ConversationMode,
  DiscoveryFeatureActor,
  DiscoveryFeatureEventRef,
  DiscoveryFeatureBullet,
  DiscoveryFeature,
  DiscoveryFeaturesPayload,
  CodeEvidence,
  CodeComparisonFeatureResult,
  CodeComparisonSummary,
  CodeComparisonPayload,
  CodeComparisonState,
  CreateSessionRefs,
  UseSessionReturn,
  DocImportResult,
} from './sessionTypes';

// ─── Hook ──────────────────────────────────────────────────────────

export function useSession(
  initialSessionId?: string,
  initialMode: ConversationMode = 'discovery',
  // 083: thread an alternate chat2 context id (e.g. 'event-storming')
  // through the one chat; omitted = the default discovery context.
  options?: { contextId?: string },
): UseSessionReturn {
  // ── Core state ─────────────────────────────────────────────────
  const [sessionId, setSessionId] = useState<string | null>(initialSessionId ?? null);
  const [mode, setMode] = useState<ConversationMode>(initialMode);
  const [sessionLoading, setSessionLoading] = useState(!!initialSessionId);
  const [sessionError, setSessionError] = useState<string | null>(null);

  const [memorySummary, setMemorySummary] = useState('');
  const [showMemoryModal, setShowMemoryModal] = useState(false);
  const [backlogPanelOpen, setBacklogPanelOpen] = useState(false);
  const [newModel, setNewModel] = useState<NewModel>({ flowText: '', openQuestions: '', nextQuestion: '', raw: '', frozenBoundary: 0 });

  // ── Activity feedback (056; owner-agreed activity feedback, 2026-07-09) ──
  // The in-flight turn's background activities, folded from `activity` SSE
  // frames by the shared reducer. Discovery emits none today; wiring the
  // handler here means the ONE panel inherits the behaviour the moment the
  // server emits. Ephemeral: a fresh turn resets the set.
  // ── Narrate → Refine (002-narrate-capture) ─────────────────────
  // `phase` is forwarded verbatim from the server; undefined ⇒ a
  // pre-feature session (the renderer defaults to refine, FR-015).
  // `spine` is always an array — absent ⇒ [] (calm narrate-empty).
  const [phase, setPhase] = useState<SessionPhase | undefined>(undefined);
  const [spine, setSpine] = useState<SpineStep[]>([]);

  // ── Agent OFFER tracking (002-narrate-capture, FR-008) ──────────
  // `turnKind` is the latest done-frame classification (authoritative).
  // `offerDismissed` hides the CURRENT offer without changing phase; it
  // resets to false on every new done frame so a subsequent done:offer
  // re-offers (per-offer dismissal, §6.2). `transitionError` surfaces a
  // failed flip so the SME is never silently stuck (P1-review nit).
  const [turnKind, setTurnKind] = useState<'question' | 'nudge' | 'offer'>('question');
  const [offerDismissed, setOfferDismissed] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);

  // Derived: the agent is offering the move to the map iff we are in
  // narrate, the last turn was classified 'offer', and the SME hasn't
  // dismissed this offer. `offered` is purely a narrate concept — an
  // offer kind while in refine never raises it (the SME is already on
  // the canvas).
  const transitionOffered = phase === 'narrate' && turnKind === 'offer' && !offerDismissed;

  // ── Refs ───────────────────────────────────────────────────────
  const sessionIdRef = useRef(sessionId);
  const sessionLoadingRef = useRef(sessionLoading);
  const modeRef = useRef(mode);

  // Keep refs in sync
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    sessionLoadingRef.current = sessionLoading;
  }, [sessionLoading]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  // ── Computed models (deterministic, no LLM) ───────────────────
  const dsnModel: DsnModel = useMemo(() => {
    return parseDsn(memorySummary);
  }, [memorySummary]);

  // ── Discovery feature extraction ──────────────────────────────
  const {
    discoveryFeatures,
    setDiscoveryFeatures,
    isExtractingFeatures,
    refreshDiscoveryFeatures,
    triggerDiscoveryFeatureExtraction,
  } = useDiscoveryFeatures({
    sessionIdRef,
    modeRef,
    flowText: newModel.flowText,
  });

  // ── Imports (repo + document + mermaid) + manual flow edit ──
  // ── VSM edits (inline metric editor + "add to VSM" affordance) ──
  // Single-shot save: client owns the full vsm object in newModel,
  // mutates it locally, and posts it back as a whole. Server-side
  // validation enforces shape, but optimistic local update means the
  // canvas reflects the change before the round-trip lands.
  const saveVsm = useCallback(async (nextVsm: import('../utils/flowModel').Vsm): Promise<{ ok: boolean; error?: string }> => {
    const id = sessionIdRef.current;
    if (!id) return { ok: false, error: 'No active session' };
    setNewModel((prev) => ({ ...prev, vsm: nextVsm }));
    try {
      const res = await fetch(`/api/sessions/${id}/vsm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vsm: nextVsm }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { ok: false, error: data.error || `HTTP ${res.status}` };
      }
      return { ok: true };
    } catch (err) {
      return { ok: false, error: (err as Error).message };
    }
  }, [sessionIdRef]);

  // ── Shared turn-result applier (003-narrate-voice-feedback) ────
  // The SINGLE writer of the chat2 `done` frame's per-turn fields:
  // newModel (stripped of phase/spine/kind), phase (only for an
  // explicit 'narrate'|'refine' — absent leaves it as-is), spine
  // (absent ⇒ []), turnKind (absent ⇒ 'question'), and the per-offer
  // dismissal reset (so a fresh done:offer re-offers). Extracted from
  // handleTextMessage's onDone so BOTH the text path and the voice path
  // apply the frame identically — no duplicated field-threading
  // (FR-001..FR-004, FR-003 reuse). Behaviour-preserving for the text
  // path (constitution VI; pinned by the CHAR (text applier) tests).
  const applyTurnResult = useCallback(
    (doneModel: DoneModel) => {
      // 054 (T045, additive): `factEvents` is the facilitated-session sibling
      // (threaded to the client replica via the vision chat hook's
      // onTurnFactEvents seam) — stripped here exactly like phase/spine/kind
      // so it can never leak into newModel. A discovery frame never carries
      // it, so this is byte-identical for the discovery path (T039).
      const {
        phase: donePhase,
        spine: doneSpine,
        kind: doneKind,
        factEvents: _doneFactEvents,
        ...modelOnly
      } = doneModel;
      setNewModel(modelOnly as NewModel);
      if (donePhase === 'narrate' || donePhase === 'refine') {
        setPhase(donePhase);
      }
      setSpine(Array.isArray(doneSpine) ? doneSpine : []);
      setTurnKind(doneKind ?? 'question');
      setOfferDismissed(false);
    },
    [],
  );

  // ── Voice (Realtime API) ──────────────────────────────────────
  const dismissOffer = useCallback(() => {
    setOfferDismissed(true);
  }, []);

  const chat = useChat({
    kind: 'discovery',
    sessionId,
    mode,
    contextId: options?.contextId,
    memorySummary,
    newModel,
    setNewModel,
    setDiscoveryFeatures,
    applyTurnResult,
    transitionOffered,
    dismissOffer,
    sessionError,
  });

  // ── Load session ──────────────────────────────────────────────
  useEffect(() => {
    // Abort any in-flight turn from a prior session so a late onDone
    // can't land on the session we're about to load.
    const loadSession = async () => {
      if (!sessionId) {
        setSessionLoading(false);
        return;
      }

      // Skip loading if we're not in a loading state (session was just created locally)
      if (!sessionLoadingRef.current) return;

      try {
        const response = await fetch(`/api/sessions/${sessionId}`);
        if (response.ok) {
          const session: SessionData = await response.json();
          setSessionError(null);
          chat.replaceConversationHistory(session.conversationHistory || []);
          setMemorySummary(session.memorySummary || '');
          setNewModel(coerceNewModel(session.newModel));
          setDiscoveryFeatures(coerceDiscoveryFeatures(session.discoveryFeatures, session.featuresStatus));
          if (session.mode) setMode(session.mode as ConversationMode);
          // Narrate→Refine: phase + spine are top-level on the GET
          // response (spine is NOT nested in newModel). Forward phase
          // verbatim (undefined for pre-feature sessions); default an
          // absent spine to [] so the narrate-empty state stays calm.
          setPhase(
            session.phase === 'narrate' || session.phase === 'refine'
              ? session.phase
              : undefined,
          );
          setSpine(Array.isArray(session.spine) ? session.spine : []);
          // A freshly-loaded session has no outstanding offer until a
          // turn classifies one; clear any stale offer/error state.
          setTurnKind('question');
          setOfferDismissed(false);
          setTransitionError(null);
        } else {
          const data = await response.json().catch(() => ({}));
          const message =
            typeof data.error === 'string' && data.error.trim()
              ? data.error
              : `HTTP ${response.status}`;
          setSessionError(message);
          console.error('Failed to load session:', message);
        }
      } catch (error) {
        setSessionError((error as Error).message || 'Failed to load session');
        console.error('Error loading session:', error);
      } finally {
        setSessionLoading(false);
      }
    };

    loadSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // ── Abort any in-flight turn on unmount ───────────────────────
  // ── createSession ─────────────────────────────────────────────
  const createSession = useCallback(async (
    sessionMode: ConversationMode,
    refs: CreateSessionRefs = {},
  ): Promise<string> => {
    const body = {
      mode: sessionMode,
      ...(refs.engagementId ? { engagementId: refs.engagementId } : {}),
      ...(refs.streamId ? { streamId: refs.streamId } : {}),
    };
    const res = await fetch('/api/sessions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Failed to create session');
    const session = await res.json();
    const newId = session.id as string;
    chat.resetConversationHistory();
    setMemorySummary('');
    // A brand-new session opens in narrate with an empty spine
    // (002-narrate-capture); the first turns build the spine. No offer
    // is outstanding yet, and any prior flip error is stale.
    setPhase('narrate');
    setSpine([]);
    setTurnKind('question');
    setOfferDismissed(false);
    setTransitionError(null);
    setSessionError(null);
    setSessionLoading(false); // session is already created — nothing to load
    setMode(sessionMode);
    modeRef.current = sessionMode; // sync ref immediately — useEffect is too late
    sessionIdRef.current = newId;
    setSessionId(newId); // triggers useEffect but loadSession will find the empty session we just created
    return newId;
  }, [chat]);

  // ── restartSession ────────────────────────────────────────────
  const restartSession = useCallback(async () => {
    try {
      const newId = await createSession(modeRef.current);
      // Caller (shell) can use the sessionId state change to update URL
      void newId;
    } catch (err) {
      console.error('Error restarting session:', err);
    }
  }, [createSession]);

  // ── acceptTransition (flip narrate → refine) ──────────────────
  // POSTs the deterministic phase endpoint; on success flips local
  // phase to 'refine' so the shell reveals the canvas (FR-011/FR-012)
  // and clears any prior error. On failure (non-ok OR thrown) it keeps
  // phase at narrate and surfaces transitionError so the SME is never
  // silently stuck (P1-review nit). Backs both the always-available
  // manual flip and the agent's two-choice offer.
  const acceptTransition = useCallback(async (): Promise<void> => {
    const id = sessionIdRef.current;
    if (!id) return;
    try {
      const res = await fetch(`/api/sessions/${id}/phase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phase: 'refine' }),
      });
      if (res.ok) {
        setPhase('refine');
        setTransitionError(null);
      } else {
        const data = await res.json().catch(() => ({}));
        setTransitionError(data.error || `Couldn't open the map (HTTP ${res.status}). Try again.`);
      }
    } catch (err) {
      setTransitionError((err as Error).message || "Couldn't open the map. Try again.");
    }
  }, [sessionIdRef]);

  // ── dismissOffer (decline / keep narrating, §6.2) ─────────────
  // Hides the CURRENT agent offer without touching phase or spine; the
  // SME stays in narrate and keeps talking. Per-offer: the flag resets
  // on the next done frame so a later done:offer re-offers.
  // ── downloadTranscript ────────────────────────────────────────
  const downloadTranscript = useCallback(() => {
    const header = dsnModel.header;
    const title = header.context || 'Discovery Session';
    const date = new Date().toISOString().slice(0, 10);
    const lines: string[] = [
      `# ${title} — Transcript`,
      '',
      `**Session:** ${sessionIdRef.current}`,
      `**Date:** ${date}`,
      header.focus ? `**Focus:** ${header.focus}` : '',
      header.status ? `**Status:** ${header.status}` : '',
      '',
      '---',
      '',
    ].filter(Boolean);

    for (const msg of chat.conversationHistory) {
      const who = msg.type === 'user' ? '🧑 **You**' : '🤖 **ArcPilot**';
      lines.push(`### ${who}  \`${msg.timestamp}\``);
      lines.push('');
      lines.push(msg.text);
      lines.push('');
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}-transcript-${date}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [chat.conversationHistory, dsnModel]);

  // ── Return ────────────────────────────────────────────────────
  return {
    chat,
    // State
    sessionId,
    mode,
    sessionLoading,
    sessionError: chat.sessionError,
    conversationHistory: chat.conversationHistory,
    memorySummary,
    newModel,
    discoveryFeatures,
    dsnModel,
    isLoading: chat.isLoading,
    inputBusy: chat.inputBusy,
    isExtractingFeatures,
    callStatus: chat.callStatus,
    phase,
    spine,
    turnKind,
    transitionOffered,
    transitionError,
    // 056 activity feedback (owner-agreed activity feedback, 2026-07-09).
    activities: chat.activities,

    // Actions
    createSession,
    acceptTransition,
    dismissOffer,
    applyTurnResult,
    handleTextMessage: chat.handleTextMessage,
    startVoiceCall: chat.startVoiceCall,
    endVoiceCall: chat.endVoiceCall,
    downloadTranscript,
    restartSession,
    setShowMemoryModal,
    showMemoryModal,
    setBacklogPanelOpen,
    backlogPanelOpen,
    refreshDiscoveryFeatures,
    triggerDiscoveryFeatureExtraction,
    importFromDocument: chat.importFromDocument,
    isImportingDocument: chat.isImportingDocument,
    importFromMermaid: chat.importFromMermaid,
    isImportingMermaid: chat.isImportingMermaid,
    setFlowText: chat.setFlowText,
    isSavingFlowText: chat.isSavingFlowText,
    saveVsm,
  };
}
